import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Save, X, HelpCircle, Search, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';

interface FAQ {
  _id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
  isActive: boolean;
  createdBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

const categories = [
  'Getting Started',
  'Safety & Privacy',
  'Messaging',
  'Payments & Plans',
  'Features',
  'Account'
];

export default function AdminFAQ() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'Getting Started',
    order: 0,
    isActive: true
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const filterFAQs = useCallback(() => {
    let filtered = [...faqs];
    
    if (searchQuery) {
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }
    
    setFilteredFaqs(filtered);
  }, [faqs, searchQuery, selectedCategory]);

  useEffect(() => {
    filterFAQs();
  }, [filterFAQs]);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getAllFAQsAdmin();
      if (response.success) {
        setFaqs(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching FAQs:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: 'Getting Started',
      order: 0,
      isActive: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (faq: FAQ) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order,
      isActive: faq.isActive
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.question || !formData.answer) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (editingFaq) {
        // Update existing FAQ
        const response = await apiClient.updateFAQ(editingFaq._id, formData);
        if (response.success) {
          toast.success('FAQ updated successfully');
          fetchFAQs();
          setIsDialogOpen(false);
        }
      } else {
        // Create new FAQ
        const response = await apiClient.createFAQ(formData);
        if (response.success) {
          toast.success('FAQ created successfully');
          fetchFAQs();
          setIsDialogOpen(false);
        }
      }
    } catch (error: any) {
      console.error('Error saving FAQ:', error);
      toast.error(error.response?.data?.message || 'Failed to save FAQ');
    }
  };

  const handleDelete = async (faqId: string) => {
    try {
      const response = await apiClient.deleteFAQ(faqId);
      if (response.success) {
        toast.success('FAQ deleted successfully');
        fetchFAQs();
        setDeleteConfirmId(null);
      }
    } catch (error: any) {
      console.error('Error deleting FAQ:', error);
      toast.error(error.response?.data?.message || 'Failed to delete FAQ');
    }
  };

  const toggleActive = async (faq: FAQ) => {
    try {
      const response = await apiClient.updateFAQ(faq._id, {
        ...faq,
        isActive: !faq.isActive
      });
      if (response.success) {
        toast.success(`FAQ ${!faq.isActive ? 'activated' : 'deactivated'}`);
        fetchFAQs();
      }
    } catch (error: any) {
      console.error('Error toggling FAQ status:', error);
      toast.error(error.response?.data?.message || 'Failed to update FAQ');
    }
  };

  const getCategoryCount = (category: string) => {
    if (category === 'all') return faqs.length;
    return faqs.filter(faq => faq.category === category).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">FAQ Management</h2>
          <p className="text-gray-600 mt-1">Manage help section frequently asked questions</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-rose-600 hover:bg-rose-700">
          <Plus className="h-4 w-4 mr-2" />
          Add New FAQ
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total FAQs</CardDescription>
            <CardTitle className="text-3xl">{faqs.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active</CardDescription>
            <CardTitle className="text-3xl text-green-600">
              {faqs.filter(f => f.isActive).length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Inactive</CardDescription>
            <CardTitle className="text-3xl text-gray-400">
              {faqs.filter(f => !f.isActive).length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Categories</CardDescription>
            <CardTitle className="text-3xl">{categories.length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories ({getCategoryCount('all')})</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat} ({getCategoryCount(cat)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* FAQs List */}
      <div className="space-y-3">
        {filteredFaqs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No FAQs found. Create your first FAQ to get started.</p>
            </CardContent>
          </Card>
        ) : (
          filteredFaqs.map((faq) => (
            <Card key={faq._id} className={!faq.isActive ? 'opacity-60' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary">{faq.category}</Badge>
                          <Badge variant={faq.isActive ? 'default' : 'secondary'} className={faq.isActive ? 'bg-green-100 text-green-700' : ''}>
                            {faq.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <span className="text-xs text-gray-500">Order: {faq.order}</span>
                        </div>
                        <h3 className="font-semibold text-lg mb-2 flex items-start gap-2">
                          <HelpCircle className="h-5 w-5 text-rose-600 mt-0.5 flex-shrink-0" />
                          {faq.question}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>

                    {faq.createdBy && (
                      <div className="text-xs text-gray-500">
                        Created by {faq.createdBy.firstName} {faq.createdBy.lastName}
                        {faq.createdAt && ` • ${new Date(faq.createdAt).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(faq)}
                      title={faq.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <Check className={`h-4 w-4 ${faq.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(faq)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(faq._id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFaq ? 'Edit FAQ' : 'Create New FAQ'}
            </DialogTitle>
            <DialogDescription>
              {editingFaq ? 'Update the FAQ details below' : 'Add a new frequently asked question'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Question *</Label>
              <Input
                placeholder="Enter the question..."
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Answer *</Label>
              <Textarea
                placeholder="Enter the detailed answer..."
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                rows={6}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
                <p className="text-xs text-gray-500">Lower numbers appear first</p>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <span className="text-sm">{formData.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-rose-600 hover:bg-rose-700">
              <Save className="h-4 w-4 mr-2" />
              {editingFaq ? 'Update FAQ' : 'Create FAQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this FAQ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete FAQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}